import { useEffect, useMemo, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { api, apiWrite } from '../lib/api'
import { Empty, PageHead, RiskBar, Skel, Stat } from '../components/ui'
import { number, pct, thaiDateTime } from '../lib/format'

/**
 * Performance advisor (part 20).
 *
 * The numbers on this page come from PostgreSQL's cumulative statistics, which are cluster-wide:
 * every tenant shares the same physical tables, so a snapshot cannot be attributed to one tenant.
 * The snapshot row is stored under the caller's tenant (the API repeats this in scope_note) and
 * the page says so at the top. Nothing here executes DDL — accepting a proposal records a
 * decision, and an operator runs the statement after review.
 *
 * The page deliberately shows the diagnostic query behind every proposal. A score without
 * provenance is not reviewable six weeks later, which is why the API stores the exact SQL.
 */

type PerfKind = 'missing_fk_index' | 'seq_scan_hot' | 'unused_index' | 'slow_query'
type PerfStatus = 'proposed' | 'accepted' | 'rejected'

interface BloatEstimate {
  dead_ratio: number
  reclaimable_bytes: number
  level: 'ok' | 'watch' | 'vacuum' | 'reindex'
}

interface PerfTable {
  schema: string
  name: string
  live_tuples: number
  dead_tuples: number
  dead_ratio: number
  seq_scans: number
  idx_scans: number
  seq_scan_ratio: number
  rows_per_seq_scan: number
  table_bytes: number
  index_bytes: number
  total_bytes: number
  bloat: BloatEstimate
  verdict: string
  last_autovacuum?: string
  last_autoanalyze?: string
}

interface SlowQuery {
  rank: number
  query_id?: string
  query: string
  calls: number
  rows: number
  total_exec_ms: number
  mean_exec_ms: number
  max_exec_ms: number
}

interface PerfSnapshot {
  id: string
  captured_at: string
  duration_ms: number
  scope: string
  pg_version: string
  database_name: string
  stats_reset_at?: string
  slow_query_source: 'pg_stat_statements' | 'unavailable'
  slow_query_fallback?: string
  table_count: number
  total_bytes: number
  live_tuples: number
  dead_tuples: number
  seq_scans: number
  idx_scans: number
  blocks_read: number
  blocks_hit: number
  deadlocks: number
  temp_bytes: number
  proposal_count: number
  tables?: PerfTable[]
  slow_queries?: SlowQuery[]
}

interface PerfProposal {
  id: string
  snapshot_id: string
  fingerprint: string
  kind: PerfKind
  table_schema: string
  table_name: string
  index_name?: string
  columns: string[]
  ddl: string
  requires_columns: boolean
  evidence_query: string
  query_sample?: string
  reason_code: string
  rationale: string
  score: number
  confidence: number
  estimated_bytes: number
  metrics: Record<string, number>
  status: PerfStatus
  occurrences: number
  first_seen_at: string
  last_seen_at: string
  decided_by?: string
  decided_at?: string
  decision_note?: string
}

interface PerfOverview {
  latest_snapshot: PerfSnapshot | null
  has_snapshot: boolean
  proposals: PerfProposal[]
  proposal_counts: Record<string, number>
  scope: string
  stored_under_tenant: string
  scope_note: string
  capture_hint: string
}

interface PerfProposalList {
  proposals: PerfProposal[]
  proposal_counts: Record<string, number>
  scope: string
  read_only: boolean
}

interface CaptureResult {
  snapshot: PerfSnapshot
  proposals: PerfProposal[]
  proposal_count: number
  scope: string
  stored_under_tenant: string
  scope_note: string
  ddl_executed: boolean
}

interface PerfSnapshotList {
  snapshots: PerfSnapshot[]
}

const kindLabel: Record<PerfKind, string> = {
  missing_fk_index: 'foreign key ไม่มีดัชนีรองรับ',
  seq_scan_hot: 'ตารางถูกสแกนเต็มบ่อย',
  unused_index: 'ดัชนีไม่ถูกเรียกใช้',
  slow_query: 'คำสั่งช้า',
}

const reasonLabel: Record<string, string> = {
  fk_without_supporting_index: 'คอลัมน์ foreign key ไม่มี index ที่ขึ้นต้นด้วยคอลัมน์เดียวกัน',
  index_never_scanned: 'idx_scan = 0 นับตั้งแต่รีเซ็ตสถิติ',
  sequential_scan_pressure: 'seq_scan สูงเมื่อเทียบกับ idx_scan และแต่ละครั้งอ่านหลายแถว',
  high_total_execution_time: 'เวลารวมของคำสั่งสูง แม้ค่าเฉลี่ยต่อครั้งอาจไม่สูง',
}

const statusLabel: Record<PerfStatus, string> = {
  proposed: 'รอตัดสิน',
  accepted: 'ยอมรับแล้ว',
  rejected: 'ปฏิเสธแล้ว',
}

const verdictLabel: Record<string, string> = {
  ok: 'ปกติ',
  watch: 'เฝ้าระวัง',
  vacuum: 'ควร VACUUM',
  reindex: 'ควร REINDEX',
  review_indexes: 'ควรทบทวนดัชนี',
}

const verdictTone: Record<string, string> = {
  ok: 'sev-low',
  watch: 'sev-medium',
  vacuum: 'sev-high',
  reindex: 'sev-critical',
  review_indexes: 'sev-medium',
}

const fallbackLabel: Record<string, string> = {
  extension_not_installed: 'ยังไม่ได้ติดตั้งส่วนขยาย pg_stat_statements',
  not_preloaded: 'ติดตั้งส่วนขยายแล้ว แต่ยังไม่ได้โหลดผ่าน shared_preload_libraries',
  permission_denied: 'บัญชีแอปไม่มีสิทธิ์อ่าน pg_stat_statements',
  view_missing: 'ไม่พบ view ของ pg_stat_statements ใน search_path',
  probe_failed: 'ตรวจสอบส่วนขยายไม่สำเร็จ',
  query_failed: 'อ่าน pg_stat_statements ไม่สำเร็จ',
  read_failed: 'อ่านผลลัพธ์ pg_stat_statements ไม่สำเร็จ',
  scan_failed: 'แปลงผลลัพธ์ pg_stat_statements ไม่สำเร็จ',
}

function bytes(n: number): string {
  if (!Number.isFinite(n) || n <= 0) return '0 B'
  if (n >= 1 << 30) return `${(n / (1 << 30)).toFixed(2)} GB`
  if (n >= 1 << 20) return `${(n / (1 << 20)).toFixed(1)} MB`
  if (n >= 1 << 10) return `${(n / (1 << 10)).toFixed(0)} KB`
  return `${number(n)} B`
}

function duration(ms: number): string {
  if (ms <= 0) return 'น้อยกว่า 1 วินาที'
  if (ms < 1000) return `${number(ms)} มิลลิวินาที`
  if (ms < 60_000) return `${(ms / 1000).toFixed(1)} วินาที`
  return `${(ms / 60_000).toFixed(1)} นาที`
}

function timeText(iso: string | null | undefined): string {
  return iso ? thaiDateTime(iso) : '—'
}

function errorText(err: unknown, fallback: string): string {
  return err instanceof Error ? err.message : fallback
}

function objectName(p: PerfProposal): string {
  if (!p.table_name) return 'ไม่ทราบตาราง'
  return p.table_schema ? `${p.table_schema}.${p.table_name}` : p.table_name
}

function toneForScore(score: number): string {
  if (score >= 80) return 'sev-critical'
  if (score >= 60) return 'sev-high'
  if (score >= 35) return 'sev-medium'
  return 'sev-low'
}

export function Perf() {
  const qc = useQueryClient()
  const [status, setStatus] = useState<PerfStatus>('proposed')
  const [kind, setKind] = useState<'' | PerfKind>('')
  const [selected, setSelected] = useState<string | null>(null)
  const [note, setNote] = useState('')
  const [notice, setNotice] = useState<string | null>(null)
  const [actionError, setActionError] = useState<string | null>(null)

  const overview = useQuery({
    queryKey: ['perf-overview'],
    queryFn: () => api<PerfOverview>('/v1/perf/overview'),
    staleTime: 15_000,
  })

  const proposals = useQuery({
    queryKey: ['perf-proposals', status, kind],
    queryFn: () =>
      api<PerfProposalList>(
        `/v1/perf/proposals?status=${status}${kind ? `&kind=${kind}` : ''}&limit=100`,
      ),
    staleTime: 15_000,
  })

  const snapshots = useQuery({
    queryKey: ['perf-snapshots'],
    queryFn: () => api<PerfSnapshotList>('/v1/perf/snapshots?limit=8'),
    staleTime: 30_000,
  })

  const rows = proposals.data?.proposals ?? []
  const latest = overview.data?.latest_snapshot ?? null
  const tables = useMemo(
    () => [...(latest?.tables ?? [])].sort((a, b) => b.total_bytes - a.total_bytes),
    [latest],
  )

  useEffect(() => {
    if (selected && rows.some((row) => row.id === selected)) return
    setSelected(rows.length > 0 ? rows[0].id : null)
  }, [rows, selected])

  const selectedProposal = useMemo(
    () => rows.find((row) => row.id === selected) ?? null,
    [rows, selected],
  )

  useEffect(() => {
    setNote('')
  }, [selected])

  const refresh = () => {
    void qc.invalidateQueries({ queryKey: ['perf-overview'] })
    void qc.invalidateQueries({ queryKey: ['perf-proposals'] })
    void qc.invalidateQueries({ queryKey: ['perf-snapshots'] })
  }

  const capture = useMutation({
    mutationFn: () => apiWrite<CaptureResult>('/v1/perf/snapshots', 'POST'),
    onSuccess: (result) => {
      setActionError(null)
      setNotice(
        `เก็บสแนปช็อตแล้ว: ${result.snapshot.table_count} ตาราง · ${result.proposal_count} ข้อเสนอใหม่ ` +
        `ใช้เวลา ${duration(result.snapshot.duration_ms)} · ไม่มีการสร้างหรือลบดัชนีอัตโนมัติ`,
      )
      refresh()
    },
    onError: (err) => {
      setActionError(errorText(err, 'เก็บสแนปช็อตไม่สำเร็จ'))
    },
  })

  const decide = useMutation({
    mutationFn: (input: { id: string; status: 'accepted' | 'rejected'; note: string }) =>
      apiWrite<{ proposal: PerfProposal }>(
        `/v1/perf/proposals/${encodeURIComponent(input.id)}/decision`,
        'POST',
        { status: input.status, note: input.note },
      ),
    onSuccess: (result) => {
      setActionError(null)
      setNotice(
        result.proposal.status === 'accepted'
          ? `ยอมรับข้อเสนอ ${objectName(result.proposal)} แล้ว — ยังไม่มีการรัน DDL ต้องให้ผู้ดูแลรันด้วยตนเอง`
          : `ปฏิเสธข้อเสนอ ${objectName(result.proposal)} แล้ว`,
      )
      setNote('')
      refresh()
    },
    onError: (err) => {
      setActionError(errorText(err, 'บันทึกผลการตัดสินไม่สำเร็จ'))
    },
  })

  const copyDDL = async (ddl: string) => {
    try {
      await navigator.clipboard.writeText(ddl)
      setNotice('คัดลอก DDL ไปยังคลิปบอร์ดแล้ว — ตรวจสอบก่อนรันบนฐานข้อมูลจริง')
    } catch {
      setActionError('คัดลอกไม่สำเร็จ เบราว์เซอร์อาจไม่อนุญาตให้ใช้คลิปบอร์ด')
    }
  }

  const counts = overview.data?.proposal_counts ?? { proposed: 0, accepted: 0, rejected: 0 }
  const busy = capture.isPending || decide.isPending
  const fallback = latest?.slow_query_fallback ?? ''
  const slowAvailable = latest?.slow_query_source === 'pg_stat_statements'

  return (
    <>
      <PageHead
        title="ที่ปรึกษาประสิทธิภาพฐานข้อมูล"
        sub="อ่านสถิติคลัสเตอร์ของ PostgreSQL · เสนอทางเลือกดัชนีพร้อมหลักฐาน · ไม่สร้างหรือลบดัชนีให้อัตโนมัติ"
        actions={
          <div className="row" style={{ gap: 10 }}>
            <span className="dim" style={{ fontSize: 11.5 }}>
              {latest ? `สแนปช็อตล่าสุด ${thaiDateTime(latest.captured_at)}` : 'ยังไม่มีสแนปช็อต'}
            </span>
            <button
              className="btn btn-primary"
              onClick={() => capture.mutate()}
              disabled={busy}
            >
              {capture.isPending ? 'กำลังเก็บข้อมูล…' : 'เก็บสแนปช็อต'}
            </button>
          </div>
        }
      />

      <div className="card card-pad" style={{ marginBottom: 16, borderColor: 'var(--color-hairline-strong)' }}>
        <div className="row" style={{ gap: 10, alignItems: 'flex-start' }}>
          <span className="chip">ระดับคลัสเตอร์</span>
          <div>
            <div style={{ fontSize: 12.5 }}>
              สถิติทั้งหมดเป็นค่าระดับคลัสเตอร์ เพราะทุก tenant ใช้ตารางจริงร่วมกัน
              สแนปช็อตจึงถูกบันทึกภายใต้ tenant ที่เรียกใช้ และไม่ได้กรองข้อมูลตาม tenant
            </div>
            <div className="dim" style={{ fontSize: 11, marginTop: 4 }}>
              {overview.data?.scope_note ?? 'อ่าน pg_stat_* แบบอ่านอย่างเดียว · ไม่มี DDL อัตโนมัติ'}
            </div>
          </div>
        </div>
      </div>

      {notice ? (
        <div className="card card-pad" role="status" style={{ marginBottom: 14, borderColor: 'var(--color-hairline-strong)' }}>
          <span className="dim" style={{ fontSize: 12.5 }}>{notice}</span>
        </div>
      ) : null}

      {actionError ? (
        <div className="card card-pad" role="alert" style={{ marginBottom: 14, borderColor: 'var(--color-crimson)' }}>
          <span style={{ fontSize: 12.5, color: 'var(--color-crimson)' }}>{actionError}</span>
        </div>
      ) : null}

      {overview.isLoading ? (
        <div className="grid-4 stagger">
          <Skel h={96} /><Skel h={96} /><Skel h={96} /><Skel h={96} />
        </div>
      ) : overview.isError ? (
        <div className="card card-pad">
          <Empty
            title="โหลดข้อมูลประสิทธิภาพไม่สำเร็จ"
            body={errorText(overview.error, 'เรียก /v1/perf/overview ไม่สำเร็จ — ตรวจสอบว่าเส้นทางถูก mount แล้ว')}
          />
        </div>
      ) : (
        <>
          <div className="grid-4 stagger">
            <Stat
              label="ขนาดข้อมูลทั้งหมด"
              value={latest ? bytes(latest.total_bytes) : '—'}
              foot={`${latest ? number(latest.table_count) : 0} ตาราง · ${latest ? bytes(latest.dead_tuples > 0 ? Math.round(latest.total_bytes * (latest.dead_tuples / Math.max(1, latest.live_tuples + latest.dead_tuples))) : 0) : '0 B'} ประมาณการพื้นที่ตาย`}
            />
            <Stat
              label="ข้อเสนอที่รอตัดสิน"
              value={number(counts.proposed ?? 0)}
              unit="รายการ"
              foot={`ยอมรับแล้ว ${number(counts.accepted ?? 0)} · ปฏิเสธแล้ว ${number(counts.rejected ?? 0)}`}
            />
            <Stat
              label="สัดส่วน sequential scan"
              value={latest && latest.seq_scans + latest.idx_scans > 0 ? pct(latest.seq_scans / (latest.seq_scans + latest.idx_scans)) : '—'}
              foot={`seq ${latest ? number(latest.seq_scans) : 0} · idx ${latest ? number(latest.idx_scans) : 0}`}
            />
            <Stat
              label="ที่มาของคำสั่งช้า"
              value={slowAvailable ? 'pg_stat_statements' : 'ไม่มี'}
              foot={
                slowAvailable
                  ? `จัดอันดับ ${latest?.slow_queries?.length ?? 0} คำสั่งตามเวลารวม`
                  : fallbackLabel[fallback] ?? 'ยังไม่มีข้อมูลคำสั่งช้า'
              }
            />
          </div>

          {!latest ? (
            <div className="card card-pad" style={{ marginTop: 18 }}>
              <Empty
                title="ยังไม่มีสแนปช็อต"
                body={overview.data?.capture_hint ?? 'กด “เก็บสแนปช็อต” เพื่ออ่าน pg_stat_user_tables, pg_stat_user_indexes และ pg_stat_database หนึ่งครั้ง'}
              />
            </div>
          ) : (
            <>
              {!slowAvailable ? (
                <div className="card card-pad" style={{ marginTop: 16 }}>
                  <div className="row" style={{ gap: 10 }}>
                    <span className="sev sev-medium"><i className="sev-dot" />ข้อมูลคำสั่งช้าไม่พร้อม</span>
                    <span className="dim" style={{ fontSize: 12 }}>
                      {fallbackLabel[fallback] ?? 'ไม่สามารถอ่าน pg_stat_statements ได้'} —
                      การวิเคราะห์ตารางและดัชนียังทำงานตามปกติ ส่วนนี้จะว่างจนกว่าจะเปิดส่วนขยาย
                    </span>
                  </div>
                </div>
              ) : null}

              <div className="card" style={{ marginTop: 18 }}>
                <div className="row" style={{ padding: '16px 20px', gap: 12 }}>
                  <span className="card-label">สุขภาพตาราง</span>
                  <span className="dim" style={{ marginLeft: 'auto', fontSize: 11.5 }}>
                    แสดง {Math.min(tables.length, 25)} จาก {tables.length} ตาราง · เรียงตามขนาด · รีเซ็ตสถิติเมื่อ {latest.stats_reset_at ? thaiDateTime(latest.stats_reset_at) : 'ไม่ทราบ'}
                  </span>
                </div>

                {tables.length === 0 ? (
                  <div style={{ padding: 20 }}>
                    <Empty title="ไม่พบตารางของผู้ใช้" body="pg_stat_user_tables ไม่มีแถวสำหรับฐานข้อมูลนี้" />
                  </div>
                ) : (
                  <div className="table-wrap" style={{ border: 'none', borderRadius: 0 }}>
                    <table className="data">
                      <thead>
                        <tr>
                          <th>ตาราง</th>
                          <th style={{ width: 120 }}>ขนาดรวม</th>
                          <th style={{ width: 150 }}>แถวมีชีวิต / ตาย</th>
                          <th style={{ width: 170 }}>สแกนเต็ม / ดัชนี</th>
                          <th style={{ width: 150 }}>อัตราแถวตาย</th>
                          <th style={{ width: 150 }}>สถานะ</th>
                          <th style={{ width: 150 }}>AUTOVACUUM ล่าสุด</th>
                        </tr>
                      </thead>
                      <tbody>
                        {tables.slice(0, 25).map((t) => (
                          <tr key={`${t.schema}.${t.name}`}>
                            <td>
                              <div className="mono" style={{ fontSize: 12 }}>{t.schema}.{t.name}</div>
                              <div className="dim" style={{ fontSize: 10.5, marginTop: 2 }}>
                                {t.last_autoanalyze ? `ANALYZE ${thaiDateTime(t.last_autoanalyze)}` : 'ยังไม่มี ANALYZE อัตโนมัติ'}
                              </div>
                            </td>
                            <td className="tnum">{bytes(t.total_bytes)}</td>
                            <td className="tnum">
                              {number(t.live_tuples)} <span className="dim">/ {number(t.dead_tuples)}</span>
                            </td>
                            <td className="tnum">
                              {number(t.seq_scans)} <span className="dim">/ {number(t.idx_scans)}</span>
                              <div className="dim" style={{ fontSize: 10.5 }}>
                                seq {pct(t.seq_scan_ratio)} · {number(Math.round(t.rows_per_seq_scan))} แถว/ครั้ง
                              </div>
                            </td>
                            <td>
                              <div className="row" style={{ gap: 8 }}>
                                <span className="bar" style={{ width: 54 }}>
                                  <i style={{
                                    width: `${Math.min(100, Math.round(t.dead_ratio * 100))}%`,
                                    background: t.bloat.level === 'ok' ? 'var(--color-emerald)' : t.bloat.level === 'watch' ? 'var(--color-gold)' : 'var(--color-amber)',
                                  }} />
                                </span>
                                <span className="tnum">{pct(t.dead_ratio)}</span>
                              </div>
                              <div className="dim" style={{ fontSize: 10.5 }}>
                                คืนได้ประมาณ {bytes(t.bloat.reclaimable_bytes)}
                              </div>
                            </td>
                            <td>
                              <span className={`sev ${verdictTone[t.verdict] ?? 'sev-low'}`}>
                                <i className="sev-dot" />
                                {verdictLabel[t.verdict] ?? t.verdict}
                              </span>
                            </td>
                            <td className="dim" style={{ fontSize: 11 }}>
                              {t.last_autovacuum ? thaiDateTime(t.last_autovacuum) : 'ยังไม่เคย'}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>

              <div className="card" style={{ marginTop: 18 }}>
                <div className="row" style={{ padding: '16px 20px', gap: 12, flexWrap: 'wrap' }}>
                  <span className="card-label">ข้อเสนอดัชนี</span>
                  <div className="row" style={{ marginLeft: 'auto', gap: 6 }}>
                    <select
                      className="input"
                      style={{ width: 'auto', padding: '6px 10px' }}
                      value={status}
                      onChange={(e) => setStatus(e.target.value as PerfStatus)}
                      aria-label="กรองตามสถานะ"
                    >
                      <option value="proposed">รอตัดสิน</option>
                      <option value="accepted">ยอมรับแล้ว</option>
                      <option value="rejected">ปฏิเสธแล้ว</option>
                    </select>
                    <select
                      className="input"
                      style={{ width: 'auto', padding: '6px 10px' }}
                      value={kind}
                      onChange={(e) => setKind(e.target.value as '' | PerfKind)}
                      aria-label="กรองตามประเภท"
                    >
                      <option value="">ทุกประเภท</option>
                      <option value="missing_fk_index">foreign key ไม่มีดัชนี</option>
                      <option value="seq_scan_hot">สแกนเต็มบ่อย</option>
                      <option value="unused_index">ดัชนีไม่ถูกใช้</option>
                      <option value="slow_query">คำสั่งช้า</option>
                    </select>
                    <button className="btn btn-ghost btn-sm" onClick={refresh} disabled={proposals.isFetching}>
                      {proposals.isFetching ? 'กำลังโหลด…' : 'รีเฟรช'}
                    </button>
                  </div>
                </div>

                {proposals.isLoading ? (
                  <div style={{ padding: 20 }} className="stack"><Skel h={44} /><Skel h={44} /><Skel h={44} /></div>
                ) : proposals.isError ? (
                  <div style={{ padding: 20 }}>
                    <Empty title="โหลดข้อเสนอไม่สำเร็จ" body={errorText(proposals.error, 'เรียก /v1/perf/proposals ไม่สำเร็จ')} />
                  </div>
                ) : rows.length === 0 ? (
                  <div style={{ padding: 20 }}>
                    <Empty
                      title="ไม่มีข้อเสนอในตัวกรองนี้"
                      body="ลองเปลี่ยนสถานะหรือประเภท หรือเก็บสแนปช็อตใหม่หลังมีปริมาณงานจริง"
                    />
                  </div>
                ) : (
                  <div className="table-wrap" style={{ border: 'none', borderRadius: 0 }}>
                    <table className="data">
                      <thead>
                        <tr>
                          <th style={{ width: 130 }}>คะแนน</th>
                          <th style={{ width: 200 }}>ประเภท</th>
                          <th>วัตถุ</th>
                          <th style={{ width: 110 }}>ความเชื่อมั่น</th>
                          <th style={{ width: 90 }}>พบซ้ำ</th>
                          <th style={{ width: 150 }}>พบล่าสุด</th>
                          <th style={{ width: 110 }}>สถานะ</th>
                        </tr>
                      </thead>
                      <tbody>
                        {rows.map((p) => {
                          const active = p.id === selected
                          return (
                            <tr
                              key={p.id}
                              onClick={() => { setSelected(p.id); setActionError(null) }}
                              style={{ cursor: 'pointer', background: active ? 'rgba(200, 169, 106, 0.05)' : undefined }}
                            >
                              <td><RiskBar score={Math.round(p.score)} /></td>
                              <td>
                                <div className={`sev ${toneForScore(p.score)}`}>
                                  <i className="sev-dot" />
                                  {kindLabel[p.kind] ?? p.kind}
                                </div>
                              </td>
                              <td>
                                <div className="mono" style={{ fontSize: 12 }}>{objectName(p)}</div>
                                <div className="dim" style={{ fontSize: 10.5, marginTop: 2 }}>
                                  {p.index_name || (p.columns.length > 0 ? p.columns.join(', ') : 'ต้องเลือกคอลัมน์หลัง EXPLAIN')}
                                </div>
                              </td>
                              <td className="tnum">{pct(p.confidence)}</td>
                              <td className="tnum">{number(p.occurrences)}</td>
                              <td className="dim" style={{ fontSize: 11 }}>{timeText(p.last_seen_at)}</td>
                              <td>
                                <span className={`sev ${p.status === 'accepted' ? 'sev-low' : p.status === 'rejected' ? 'sev-critical' : 'sev-medium'}`}>
                                  <i className="sev-dot" />
                                  {statusLabel[p.status] ?? p.status}
                                </span>
                              </td>
                            </tr>
                          )
                        })}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>

              {selectedProposal ? (
                <div className="grid-2" style={{ marginTop: 18 }}>
                  <div className="card card-pad">
                    <div className="row" style={{ marginBottom: 12, gap: 10 }}>
                      <span className="card-label">หลักฐานและข้อความ DDL</span>
                      <span className="dim" style={{ marginLeft: 'auto', fontSize: 11 }}>
                        คะแนน {selectedProposal.score.toFixed(1)} · ความเชื่อมั่น {pct(selectedProposal.confidence)}
                      </span>
                    </div>

                    <div className="row" style={{ gap: 8, marginBottom: 10, flexWrap: 'wrap' }}>
                      <span className={`sev ${toneForScore(selectedProposal.score)}`}>
                        <i className="sev-dot" />
                        {kindLabel[selectedProposal.kind] ?? selectedProposal.kind}
                      </span>
                      <span className="chip">{reasonLabel[selectedProposal.reason_code] ?? selectedProposal.reason_code}</span>
                      {selectedProposal.estimated_bytes > 0 ? (
                        <span className="chip">ผลต่อพื้นที่ประมาณ {bytes(selectedProposal.estimated_bytes)}</span>
                      ) : null}
                    </div>

                    <p className="dim" style={{ fontSize: 12, lineHeight: 1.8 }}>
                      {selectedProposal.rationale}
                    </p>

                    {selectedProposal.requires_columns ? (
                      <div className="card card-pad" style={{ marginBottom: 12, borderColor: 'var(--color-amber)' }}>
                        <div style={{ fontSize: 12, color: 'var(--color-amber)' }}>
                          ข้อเสนอนี้ยังไม่ระบุคอลัมน์ เพราะสถิติไม่บอกเงื่อนไข WHERE
                          ต้องรัน EXPLAIN (ANALYZE, BUFFERS) บนคำสั่งที่เกี่ยวข้องก่อนตัดสินใจ
                        </div>
                      </div>
                    ) : null}

                    {selectedProposal.ddl ? (
                      <>
                        <div className="row" style={{ gap: 8, marginBottom: 8 }}>
                          <span className="card-label">DDL ที่เสนอ</span>
                          <button
                            className="btn btn-ghost btn-sm"
                            style={{ marginLeft: 'auto' }}
                            onClick={() => { void copyDDL(selectedProposal.ddl) }}
                          >
                            คัดลอก DDL
                          </button>
                        </div>
                        <pre className="mono" style={{
                          fontSize: 11.5,
                          lineHeight: 1.7,
                          whiteSpace: 'pre-wrap',
                          wordBreak: 'break-word',
                          background: 'rgba(0,0,0,0.18)',
                          padding: 12,
                          borderRadius: 8,
                          marginBottom: 12,
                        }}>{selectedProposal.ddl}</pre>
                      </>
                    ) : null}

                    {selectedProposal.query_sample ? (
                      <>
                        <div className="card-label" style={{ marginBottom: 6 }}>คำสั่งที่ทำให้พบปัญหา</div>
                        <pre className="mono" style={{
                          fontSize: 11,
                          lineHeight: 1.65,
                          whiteSpace: 'pre-wrap',
                          wordBreak: 'break-word',
                          maxHeight: 160,
                          overflow: 'auto',
                          background: 'rgba(0,0,0,0.18)',
                          padding: 10,
                          borderRadius: 8,
                          marginBottom: 12,
                        }}>{selectedProposal.query_sample}</pre>
                      </>
                    ) : null}

                    <div className="card-label" style={{ marginBottom: 6 }}>คิวรีที่ใช้เป็นหลักฐาน</div>
                    <pre className="mono" style={{
                      fontSize: 10.5,
                      lineHeight: 1.6,
                      whiteSpace: 'pre-wrap',
                      wordBreak: 'break-word',
                      maxHeight: 170,
                      overflow: 'auto',
                      background: 'rgba(0,0,0,0.18)',
                      padding: 10,
                      borderRadius: 8,
                    }}>{selectedProposal.evidence_query}</pre>
                  </div>

                  <div className="card card-pad">
                    <div className="card-label" style={{ marginBottom: 10 }}>การตัดสิน</div>
                    <div className="stack" style={{ gap: 9, fontSize: 12, marginBottom: 14 }}>
                      <div className="row" style={{ gap: 10 }}>
                        <span className="dim">สถานะ</span>
                        <span style={{ marginLeft: 'auto' }}>{statusLabel[selectedProposal.status]}</span>
                      </div>
                      <div className="row" style={{ gap: 10 }}>
                        <span className="dim">พบครั้งแรก</span>
                        <span style={{ marginLeft: 'auto' }}>{timeText(selectedProposal.first_seen_at)}</span>
                      </div>
                      <div className="row" style={{ gap: 10 }}>
                        <span className="dim">พบล่าสุด</span>
                        <span style={{ marginLeft: 'auto' }}>{timeText(selectedProposal.last_seen_at)}</span>
                      </div>
                      <div className="row" style={{ gap: 10 }}>
                        <span className="dim">ตัดสินเมื่อ</span>
                        <span style={{ marginLeft: 'auto' }}>{timeText(selectedProposal.decided_at)}</span>
                      </div>
                      {selectedProposal.decision_note ? (
                        <div className="dim" style={{ fontSize: 11.5 }}>
                          หมายเหตุ: {selectedProposal.decision_note}
                        </div>
                      ) : null}
                    </div>

                    {selectedProposal.status === 'proposed' ? (
                      <>
                        <div className="field" style={{ marginTop: 0 }}>
                          <label htmlFor="perf-note">หมายเหตุประกอบการตัดสิน (ไม่บังคับ)</label>
                          <textarea
                            id="perf-note"
                            className="input"
                            rows={3}
                            maxLength={500}
                            value={note}
                            onChange={(e) => setNote(e.target.value)}
                            placeholder="เช่น ตรวจ EXPLAIN แล้ว พบว่า query หลักกรองด้วย status + created_at"
                            style={{ width: '100%', height: 'auto', padding: '10px 12px', resize: 'vertical' }}
                          />
                        </div>
                        <div className="row" style={{ gap: 8, marginTop: 12 }}>
                          <button
                            className="btn btn-primary btn-sm"
                            disabled={busy}
                            onClick={() => decide.mutate({ id: selectedProposal.id, status: 'accepted', note })}
                          >
                            {decide.isPending ? 'กำลังบันทึก…' : 'ยอมรับข้อเสนอ'}
                          </button>
                          <button
                            className="btn btn-ghost btn-sm"
                            disabled={busy}
                            onClick={() => decide.mutate({ id: selectedProposal.id, status: 'rejected', note })}
                          >
                            ปฏิเสธ
                          </button>
                        </div>
                        <p className="dim" style={{ fontSize: 11, lineHeight: 1.7, marginTop: 12 }}>
                          การยอมรับเป็นการบันทึกการตัดสินเท่านั้น ระบบไม่รัน DDL และไม่แก้ไข platform.audit_events
                        </p>
                      </>
                    ) : (
                      <div className="dim" style={{ fontSize: 12 }}>
                        ข้อเสนอนี้ถูกตัดสินแล้ว การเก็บสแนปช็อตครั้งใหม่จะไม่เปลี่ยนสถานะเดิม
                      </div>
                    )}
                  </div>
                </div>
              ) : null}

              {slowAvailable && (latest.slow_queries?.length ?? 0) > 0 ? (
                <div className="card" style={{ marginTop: 18 }}>
                  <div className="row" style={{ padding: '16px 20px', gap: 12 }}>
                    <span className="card-label">คำสั่งที่ใช้เวลารวมสูงสุด</span>
                    <span className="dim" style={{ marginLeft: 'auto', fontSize: 11.5 }}>
                      จาก pg_stat_statements · เรียงตาม total_exec_time · ค่าคงที่ถูกแทนด้วย $n
                    </span>
                  </div>
                  <div className="table-wrap" style={{ border: 'none', borderRadius: 0 }}>
                    <table className="data">
                      <thead>
                        <tr>
                          <th style={{ width: 60 }}>อันดับ</th>
                          <th>คำสั่ง</th>
                          <th style={{ width: 110 }}>เรียก</th>
                          <th style={{ width: 120 }}>เฉลี่ย</th>
                          <th style={{ width: 130 }}>รวม</th>
                          <th style={{ width: 120 }}>สูงสุด</th>
                        </tr>
                      </thead>
                      <tbody>
                        {(latest.slow_queries ?? []).map((q) => (
                          <tr key={`${q.rank}-${q.query_id ?? q.query.slice(0, 24)}`}>
                            <td className="tnum">{q.rank}</td>
                            <td className="mono" style={{ fontSize: 11, maxWidth: 520, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={q.query}>
                              {q.query}
                            </td>
                            <td className="tnum">{number(q.calls)}</td>
                            <td className="tnum">{q.mean_exec_ms.toFixed(1)} ms</td>
                            <td className="tnum">{q.total_exec_ms.toFixed(0)} ms</td>
                            <td className="tnum">{q.max_exec_ms.toFixed(1)} ms</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              ) : null}

              <div className="card" style={{ marginTop: 18 }}>
                <div className="row" style={{ padding: '16px 20px', gap: 12 }}>
                  <span className="card-label">ประวัติสแนปช็อต</span>
                  <span className="dim" style={{ marginLeft: 'auto', fontSize: 11.5 }}>
                    เก็บถาวร · ไม่มีสิทธิ์ลบ · {latest.pg_version ? `PostgreSQL ${latest.pg_version}` : ''}
                  </span>
                </div>
                {snapshots.isLoading ? (
                  <div style={{ padding: 20 }}><Skel h={40} /></div>
                ) : (snapshots.data?.snapshots.length ?? 0) === 0 ? (
                  <div style={{ padding: 20 }}><div className="dim" style={{ fontSize: 12 }}>ยังไม่มีประวัติ</div></div>
                ) : (
                  <div className="table-wrap" style={{ border: 'none', borderRadius: 0 }}>
                    <table className="data">
                      <thead>
                        <tr>
                          <th>เก็บเมื่อ</th>
                          <th style={{ width: 110 }}>ใช้เวลา</th>
                          <th style={{ width: 100 }}>ตาราง</th>
                          <th style={{ width: 120 }}>ขนาดรวม</th>
                          <th style={{ width: 110 }}>ข้อเสนอ</th>
                          <th style={{ width: 130 }}>คำสั่งช้า</th>
                        </tr>
                      </thead>
                      <tbody>
                        {(snapshots.data?.snapshots ?? []).map((s) => (
                          <tr key={s.id}>
                            <td>{thaiDateTime(s.captured_at)}</td>
                            <td className="tnum">{duration(s.duration_ms)}</td>
                            <td className="tnum">{number(s.table_count)}</td>
                            <td className="tnum">{bytes(s.total_bytes)}</td>
                            <td className="tnum">{number(s.proposal_count)}</td>
                            <td>
                              <span className={`sev ${s.slow_query_source === 'pg_stat_statements' ? 'sev-low' : 'sev-medium'}`}>
                                <i className="sev-dot" />
                                {s.slow_query_source === 'pg_stat_statements' ? 'พร้อมใช้' : 'ไม่พร้อม'}
                              </span>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            </>
          )}
        </>
      )}
    </>
  )
}
